import * as React from "react";
import { useState, useEffect } from "react";
import { Box, Typography, MenuItem, Dialog, Switch } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  useGetFieldAdminHSDevicesQuery,
  useSaveFieldAdminHSDeviceMutation,
  useGetFieldAdminHSDayQuery,
  useCompleteFieldAdminHSDayMutation,
  useGetFieldAdminDevicesQuery,
  useSaveFieldAdminHSSectionNotesMutation,
} from "../fieldAdminApi";
import { CARD_BG, BORDER, AMBER } from "../../../Global/colors";
import { BAD_RED_TEXT, LOW_BATTERY_PERCENT, LOW_DATA_MB, installerLabel, FONT_FAMILY } from "../designTokens";
import { HSPageShell, HSSectionLabel, HSNotesBlock, HSFooterActions } from "./HSStepShell";
import { FAErrorCard, FASkeletonRows } from "../FAStates";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import TabletMacOutlinedIcon from "@mui/icons-material/TabletMacOutlined";
import { InstallerAvatar } from "./InstallerRoster";
import { FASelect } from "../StockFormShell";
import { useConfirm, FAPrimaryButton, FAGhostButton } from "../FAControls";
import { FA_DIALOG_PAPER_SX, FAEmptyState } from "../FALayout";

const TOPUP_TYPES = [
  { value: "NONE", label: "No top-up" },
  { value: "POWER_BANK", label: "Power bank" },
  { value: "DATA_TOPUP", label: "Data top-up" },
];

function clampBattery(value) {
  if (value === "" || value === null) return "";
  return Math.max(0, Math.min(100, Number(value)));
}

// Same thresholds the device register flags on, so one reading can't be "low" on the
// capture screen and clean on the register. Readings are held as strings while editing.
const isBatteryLow = (record) => record.battery_percent !== "" && Number(record.battery_percent) <= LOW_BATTERY_PERCENT;
const isDataLow = (record) => record.data_balance_mb !== "" && Number(record.data_balance_mb) <= LOW_DATA_MB;

// alerts on hs-devices.html .hs-input-unit / .dev-field-label — a label above a boxed input
// with its unit set inside the field's right edge, turning red when the reading is low.
function UnitField({ label, unit, value, warn, onChange, onBlur }) {
  return (
    <Box>
      <Typography sx={{ fontSize: "0.62rem", fontWeight: 600, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.06em", mb: "3px" }}>
        {label}
      </Typography>
      <Box sx={{ position: "relative" }}>
        <Box
          component="input"
          type="number"
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          sx={{
            width: "100%",
            // A bare <input> is content-box, so width:100% plus the unit's 32px of right
            // padding overflowed the grid column and the unit sat outside the field.
            boxSizing: "border-box",
            backgroundColor: warn ? "rgba(239,68,68,0.05)" : "#0D1117",
            border: `1px solid ${warn ? "rgba(239,68,68,0.5)" : "rgba(255,255,255,0.1)"}`,
            borderRadius: "6px",
            padding: "7px 32px 7px 10px",
            color: "#fff",
            fontFamily: FONT_FAMILY,
            fontSize: "0.86rem",
            fontVariantNumeric: "tabular-nums",
            outline: "none",
            "&:focus": { borderColor: "rgba(253,185,64,0.5)" },
          }}
        />
        <Box component="span" sx={{
          position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)",
          fontSize: "0.74rem", fontWeight: 600, pointerEvents: "none",
          color: warn ? BAD_RED_TEXT : "rgba(255,255,255,0.45)",
        }}>
          {unit}
        </Box>
      </Box>
    </Box>
  );
}

// .sum-tile — the value is the accent, not white, and a warning tints the whole tile rather
// than only its border. The sub line is what makes the number actionable: "1" says nothing on
// its own, "Bongani · 18%" says who to go and find.
function SummaryTile({ label, value, sub, warn }) {
  return (
    <Box sx={{
      backgroundColor: warn ? "rgba(239,68,68,0.04)" : CARD_BG,
      border: `1px solid ${warn ? "rgba(239,68,68,0.3)" : BORDER}`,
      borderRadius: "12px", px: "16px", py: "14px",
    }}>
      <Typography sx={{ fontSize: "0.64rem", fontWeight: 600, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.08em", mb: "4px" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, color: warn ? BAD_RED_TEXT : AMBER, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
        {value}
      </Typography>
      {sub ? (
        <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.5)", mt: "2px" }}>{sub}</Typography>
      ) : null}
    </Box>
  );
}

export default function Devices() {
  const navigate = useNavigate();
  const { data, isSuccess, isError, refetch } = useGetFieldAdminHSDevicesQuery();
  const [saveDevice, { isLoading: isSaving }] = useSaveFieldAdminHSDeviceMutation();
  const { data: day, refetch: refetchDay } = useGetFieldAdminHSDayQuery();
  const [completeDay] = useCompleteFieldAdminHSDayMutation();
  const [saveSectionNotes] = useSaveFieldAdminHSSectionNotesMutation();

  // Device-driven, not installer-driven: each row is a device the register says is in the
  // field, so the register supplies both the fleet tag and the installer name. That also
  // removes the free-text tag entry, which could silently orphan a reading from its device.
  const { data: register, isSuccess: isRegisterSuccess, isError: isRegisterError, refetch: refetchRegister } = useGetFieldAdminDevicesQuery();
  const [records, setRecords] = useState(null);
  const [sectionNotes, setSectionNotes] = useState("");
  const [openNoteFor, setOpenNoteFor] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const { ask, confirmDialog } = useConfirm();

  const nameById = React.useMemo(
    () => new Map((isRegisterSuccess ? register.installers : []).map((i) => [i.id, i.name])),
    [register, isRegisterSuccess]
  );

  useEffect(() => {
    if (!isSuccess || !isRegisterSuccess || records !== null) return;

    // One record per (day, installer) is all the backend stores, so a second device held
    // by the same installer can't get its own row today — dedupe rather than silently
    // POSTing two rows that overwrite each other.
    const seeded = new Map();
    register.devices
      .filter((device) => device.status !== "decommissioned" && device.assigned_installer_id != null)
      .forEach((device) => {
        if (seeded.has(device.assigned_installer_id)) return;
        seeded.set(device.assigned_installer_id, { fleet_asset_tag: device.fleet_id, device_type: device.device_type });
      });

    setSectionNotes(data.notes || "");
    const existing = new Map(data.records.map((r) => [r.installer_id, r]));
    const installerIds = [...new Set([...seeded.keys(), ...existing.keys()])];

    setRecords(installerIds.map((installerId) => {
      const record = existing.get(installerId);
      const device = seeded.get(installerId);
      return {
        installer_id: installerId,
        fleet_asset_tag: record?.fleet_asset_tag || device?.fleet_asset_tag || "",
        device_type: record?.device_type || device?.device_type || "Phone",
        battery_percent: record?.battery_percent ?? "",
        data_balance_mb: record?.data_balance_mb ?? "",
        topup_type: record?.topup_type || "NONE",
        topup_quantity: record?.topup_quantity || "",
        note: record?.note || "",
      };
    }));
  }, [isSuccess, data, records, register, isRegisterSuccess]);

  if (records === null) {
    return (
      <HSPageShell step={{ stepNumber: 5, title: "Device & Data Status", countLabel: "" }}>
        {/* This step's rows are built from the device register, so a failing register
            call blocks the render just as much as a failing readings call. */}
        {isError || isRegisterError
          ? <FAErrorCard onRetry={isRegisterError ? refetchRegister : refetch} />
          : <FASkeletonRows count={5} />}
      </HSPageShell>
    );
  }

  const patchRecord = (installerId, patch) => {
    setRecords((prev) => prev.map((r) => (r.installer_id !== installerId ? r : { ...r, ...patch })));
  };

  // See Attendance.saveAll — returning false keeps HSFooterActions on the page.
  const saveAll = async () => {
    const results = await Promise.all([
      ...records.map((r) => saveDevice({
        ...r,
        battery_percent: r.battery_percent === "" ? null : Number(r.battery_percent),
        data_balance_mb: r.data_balance_mb === "" ? null : Number(r.data_balance_mb),
      })),
      saveSectionNotes({ section: "devices", notes: sectionNotes }),
    ]);
    return results.every((result) => !result.error);
  };

  const handleComplete = async () => {
    const proceed = await ask({
      title: "Complete day workflow?",
      body: records.length === 0
        ? "No device readings have been recorded yet. Completing marks the H&S sequence complete and shows the day summary."
        : "Marks the H&S sequence complete and shows the day summary. This is an in-app action only \u2014 it opens no end-day page and creates no separate day record.",
      confirmLabel: "Complete workflow",
    });
    if (!proceed) return;
    // The overlay claims "all 5 sections signed for today", so it must not appear
    // unless both the readings and the completion actually persisted — otherwise the FA
    // sees the day as done while Home still shows "Continue today's workflow".
    if (!(await saveAll())) return;
    const completed = await completeDay();
    if (completed.error) return;
    await refetchDay();
    setShowOverlay(true);
  };

  const alertCount = records.filter((r) => isBatteryLow(r) || isDataLow(r)).length;

  return (
    <HSPageShell step={{ stepNumber: 5, title: "Device & Data Status", countLabel: alertCount > 0 ? `${alertCount} flagged` : `${records.length} recorded` }}>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", mb: "18px" }}>
        {(() => {
          const checked = records.filter((r) => r.battery_percent !== "" || r.data_balance_mb !== "");
          const lowBattery = records.filter(isBatteryLow);
          const lowData = records.filter(isDataLow);
          // Names the first offender and counts the rest, so the tile points somewhere.
          const who = (list, render) => {
            if (!list.length) return null;
            const first = `${installerLabel(nameById.get(list[0].installer_id), list[0].installer_id).split(" ")[0]} · ${render(list[0])}`;
            return list.length === 1 ? first : `${first} +${list.length - 1} more`;
          };
          return (
            <>
              <SummaryTile
                label="Devices checked"
                value={`${checked.length} / ${records.length}`}
                sub={records.length && checked.length === records.length ? "All devices read" : null}
              />
              <SummaryTile
                label="Battery alerts" value={lowBattery.length} warn={lowBattery.length > 0}
                sub={who(lowBattery, (r) => `${r.battery_percent}%`)}
              />
              <SummaryTile
                label="Data low" value={lowData.length} warn={lowData.length > 0}
                sub={who(lowData, (r) => `${r.data_balance_mb} MB`)}
              />
            </>
          );
        })()}
      </Box>

      <HSSectionLabel right="Phones & tablets · from the device register">Per-installer device status</HSSectionLabel>

      <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden" }}>
        {records.length === 0 ? (
          <FAEmptyState py="60px">No devices issued to this team yet — add them on the device register.</FAEmptyState>
        ) : (
          records.map((r, index) => {
            const batteryWarn = isBatteryLow(r);
            const dataWarn = isDataLow(r);
            return (
              <Box key={r.installer_id} sx={{ p: "14px 18px", borderBottom: index === records.length - 1 ? "none" : "1px solid rgba(255,255,255,0.04)", backgroundColor: (batteryWarn || dataWarn) ? "rgba(239,68,68,0.04)" : "transparent" }}>
                {/* .dev-main — one row per installer. This was a stacked form, which read as
                    five separate controls rather than one technician's kit at a glance. */}
                <Box sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "36px 1fr auto", md: "36px 1.4fr 0.6fr 1.2fr 1.2fr 1fr auto" },
                  gap: { xs: "10px", md: "14px" },
                  alignItems: "center",
                }}>
                  <InstallerAvatar name={nameById.get(r.installer_id)} id={r.installer_id} />

                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: "#fff" }}>
                      {installerLabel(nameById.get(r.installer_id), r.installer_id)}
                      {(batteryWarn || dataWarn) ? (
                        // .warn-pill — sits with the name, not off in its own column.
                        <Box component="span" sx={{
                          display: "inline-flex", alignItems: "center", ml: "6px",
                          fontSize: "0.62rem", fontWeight: 700, px: "6px", py: "2px", borderRadius: "4px",
                          backgroundColor: "rgba(239,68,68,0.14)", color: BAD_RED_TEXT,
                          textTransform: "uppercase", letterSpacing: "0.06em",
                        }}>
                          {batteryWarn ? "Battery low" : "Data low"}
                        </Box>
                      ) : null}
                    </Typography>
                    {/* .dev-id — the technician and the asset they are carrying. The fleet tag
                        alone did not say who the row was about once the name wrapped. */}
                    <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", mt: "1px" }}>
                      {`ID-${String(r.installer_id).padStart(5, "0")}`}{r.fleet_asset_tag ? ` · ${r.fleet_asset_tag}` : ""}
                    </Typography>
                  </Box>

                  {/* Read-only: Device.device_type on the register is the authority, and
                      an editable copy here would be a second, unreconciled one. */}
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", justifySelf: "flex-start", px: "9px", py: "4px", borderRadius: "5px", backgroundColor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.75)", fontSize: "0.7rem", fontWeight: 600, whiteSpace: "nowrap", gridColumn: { xs: "2 / -1", md: "auto" } }}>
                    {r.device_type === "Tablet"
                      ? <TabletMacOutlinedIcon sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.55)" }} />
                      : <PhoneAndroidOutlinedIcon sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.55)" }} />}
                    {r.device_type}
                  </Box>

                  <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
                    <UnitField
                      label="Battery" unit="%" warn={batteryWarn} value={r.battery_percent}
                      onChange={(e) => patchRecord(r.installer_id, { battery_percent: e.target.value })}
                      onBlur={(e) => patchRecord(r.installer_id, { battery_percent: clampBattery(e.target.value) })}
                    />
                  </Box>
                  <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
                    <UnitField
                      label="Data balance" unit="MB" warn={dataWarn} value={r.data_balance_mb}
                      onChange={(e) => patchRecord(r.installer_id, { data_balance_mb: e.target.value })}
                    />
                  </Box>

                  {/* .topup-block — a switch, per the design. The type select appears only once
                      it is on: the design stores a bare boolean, but the backend distinguishes a
                      power bank from a data top-up and the H&S export reads that, so the choice
                      is kept rather than collapsed. Same shape as Attendance's conduct flag. */}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "5px", gridColumn: { xs: "1 / -1", md: "auto" } }}>
                    <Typography sx={{ fontSize: "0.62rem", fontWeight: 600, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      Top-up needed
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Switch
                        size="small"
                        checked={r.topup_type !== "NONE"}
                        onChange={() => patchRecord(r.installer_id, { topup_type: r.topup_type === "NONE" ? "POWER_BANK" : "NONE" })}
                        sx={{
                          m: 0,
                          "& .MuiSwitch-switchBase.Mui-checked": { color: BAD_RED_TEXT },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: BAD_RED_TEXT, opacity: 0.5 },
                        }}
                      />
                      {r.topup_type === "NONE" ? (
                        <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.6)" }}>&mdash;</Typography>
                      ) : (
                        <FASelect
                          size="small"
                          fullWidth={false}
                          value={r.topup_type}
                          onChange={(e) => patchRecord(r.installer_id, { topup_type: e.target.value })}
                          sx={{
                            width: "auto",
                            "& .MuiSelect-select.MuiSelect-select": {
                              paddingTop: "3px", paddingBottom: "3px", paddingLeft: "8px", paddingRight: "22px",
                              backgroundColor: "#0D1117", border: "1px solid rgba(255,255,255,0.1)",
                              borderRadius: "6px", color: "#fff", fontFamily: FONT_FAMILY,
                              fontSize: "0.72rem", minHeight: "unset", lineHeight: 1.5,
                            },
                            "& .MuiOutlinedInput-notchedOutline": { border: 0 },
                            "& .MuiSelect-icon": { width: "10px", height: "10px", top: "calc(50% - 5px)", right: "6px", color: "rgba(255,255,255,0.5)" },
                          }}
                        >
                          {TOPUP_TYPES.filter((t) => t.value !== "NONE").map((t) => (
                            <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                          ))}
                        </FASelect>
                      )}
                    </Box>
                  </Box>

                  {/* .dev-note-btn — a bordered control that warms to the accent once a note
                      exists, rather than a bare text link. */}
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setOpenNoteFor(openNoteFor === r.installer_id ? null : r.installer_id)}
                    sx={{
                      justifySelf: { xs: "flex-start", md: "auto" },
                      gridColumn: { xs: "1 / -1", md: "auto" },
                      fontSize: "0.72rem", fontWeight: 500, fontFamily: FONT_FAMILY,
                      px: "10px", py: "6px", borderRadius: "6px", cursor: "pointer", whiteSpace: "nowrap",
                      backgroundColor: r.note ? "rgba(253,185,64,0.05)" : "transparent",
                      border: `1px solid ${r.note ? "rgba(253,185,64,0.3)" : "rgba(255,255,255,0.1)"}`,
                      color: r.note ? AMBER : "rgba(255,255,255,0.5)",
                      "&:hover": { color: AMBER, borderColor: "rgba(253,185,64,0.3)" },
                    }}
                  >
                    {openNoteFor === r.installer_id ? "\u2212 Hide" : r.note ? "Note \u2713" : "+ Note"}
                  </Box>
                </Box>

                {/* .dev-note-expand — indented past the avatar so it reads as belonging to
                    this row rather than starting a new one. */}
                {openNoteFor === r.installer_id ? (
                  <Box sx={{ mt: "12px", pl: { xs: 0, md: "50px" } }}>
                    <textarea
                      value={r.note}
                      onChange={(e) => patchRecord(r.installer_id, { note: e.target.value })}
                      placeholder="e.g. Battery dropping fast — issuing fleet power bank from van."
                      style={{ width: "100%", boxSizing: "border-box", resize: "vertical", minHeight: "48px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.1)", backgroundColor: "#0D1117", color: "#fff", padding: "8px 10px", fontSize: "0.82rem", fontFamily: "inherit", outline: "none" }}
                    />
                  </Box>
                ) : null}
              </Box>
            );
          })
        )}
      </Box>

      <HSNotesBlock
        value={sectionNotes}
        onChange={(e) => setSectionNotes(e.target.value)}
        sub="Fleet-level observations — recurring battery issues, suggested device swaps, data plan adjustments to escalate to IMS."
        placeholder="e.g. Two handsets losing charge by midday — request replacements from IMS."
      />

      <HSFooterActions onSaveExit={saveAll} onMarkComplete={handleComplete} markCompleteLabel="Complete day workflow →" markCompleteDisabled={isSaving} />

      {showOverlay ? (
        <DayCompleteOverlay day={day} onBack={() => setShowOverlay(false)} onHub={() => navigate("/field-admin/hs/")} />
      ) : null}
      {confirmDialog}
    </HSPageShell>
  );
}

function DayCompleteOverlay({ day, onBack, onHub }) {
  return (
    // A real Dialog, not a hand-rolled fixed-inset overlay: StockFormShell documents why
    // (no focus trap, no Escape, no scroll lock, and a bare z-index that has to out-guess
    // the bottom nav's). This was the last one left after the QE modal was converted.
    <Dialog open onClose={onBack} fullWidth sx={[FA_DIALOG_PAPER_SX, { "& .MuiDialog-paper": { maxWidth: "420px" } }]}>
      <Box sx={{ p: "28px", textAlign: "center" }}>
        <Box sx={{ width: "52px", height: "52px", borderRadius: "50%", backgroundColor: "rgba(34,197,94,0.14)", color: "#4ADE80", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: "14px" }}>
          <CheckCircleOutlineIcon sx={{ fontSize: "1.8rem" }} />
        </Box>
        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.1rem" }}>H&S day workflow complete</Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.8rem", mt: "4px" }}>All 5 sections signed for today</Typography>

        <Box sx={{ textAlign: "left", mt: "18px", display: "flex", flexDirection: "column", gap: "8px" }}>
          {/* The server's steps, the same done/not-done the hub and the alert feed show (NEU-1088). */}
          <SummaryLine label="Vehicle inspection" ok={day?.steps.vehicle} />
          <SummaryLine label="Tool inspection" ok={day?.steps.tools} detail={`${day?.tools_done ?? 0}/${day?.tools_total ?? 0} installers`} />
          <SummaryLine label="Attendance" ok={day?.steps.attendance} detail={`${day?.attendance_verified ?? 0}/${day?.attendance_total ?? 0} verified`} />
          <SummaryLine label="Toolbox talk" ok={day?.steps.toolbox} detail={`${day?.toolbox_ack_done ?? 0}/${day?.toolbox_ack_total ?? 0} acknowledged`} />
          <SummaryLine label="Device & data" ok={day?.steps.devices} detail={`${day?.devices_checked ?? 0}/${day?.devices_total ?? 0} recorded`} />
        </Box>

        <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.4)", mt: "16px" }}>
          Summary held in-app for the field admin — no separate end-day record.
        </Typography>

        <Box sx={{ display: "flex", gap: "10px", mt: "20px" }}>
          <FAGhostButton onClick={onBack} sx={{ flex: 1, py: "12px", borderRadius: "10px", fontSize: "0.85rem" }}>
            Back to review
          </FAGhostButton>
          <FAPrimaryButton onClick={onHub} sx={{ flex: 1, py: "12px", borderRadius: "10px", fontSize: "0.85rem" }}>
            Back to H&S hub
          </FAPrimaryButton>
        </Box>
      </Box>
    </Dialog>
  );
}

function SummaryLine({ label, ok, detail }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <CheckCircleOutlineIcon sx={{ fontSize: "1rem", color: ok ? "#4ADE80" : "rgba(255,255,255,0.25)" }} />
      <Typography sx={{ color: "#fff", fontSize: "0.82rem", flexGrow: 1 }}>{label}</Typography>
      {detail ? <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.76rem" }}>{detail}</Typography> : null}
    </Box>
  );
}
